import { apiRequest, onlyDigits } from './client';
import {
  CheckoutAddressData,
  CheckoutPaymentData,
  CheckoutPlan,
  CheckoutUserData,
} from '../types/CheckoutType';

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user_data: Record<string, unknown>;
}

export async function createUser(data: CheckoutUserData, isProfessional = false) {
  return apiRequest<Record<string, unknown>>('users', {
    method: 'POST',
    body: JSON.stringify({
      full_name: data.full_name.trim(),
      email: data.email.toLowerCase().trim(),
      document: onlyDigits(data.document),
      phone: onlyDigits(data.phone),
      password: data.password,
      receive_communications: data.receive_communications,
      privacy_terms: data.privacy_terms,
      program_rules: data.program_rules,
      is_professional: isProfessional,
    }),
  });
}

export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const res = await apiRequest<{ exists?: boolean }>(
      `users/exists?email=${encodeURIComponent(email.toLowerCase().trim())}`,
    );
    return !!res?.exists;
  } catch {
    return false; // na dúvida, não bloqueia o fluxo
  }
}

export async function login(email: string, password: string) {
  return apiRequest<AuthResponse>('auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email: email.toLowerCase().trim(),
      password,
    }),
  });
}

/**
 * Planos públicos — não exige autenticação.
 * O checkout novo recebe o plano já escolhido da landing (via ?plan_id=)
 * e usa esta lista só para montar o resumo / permitir troca de plano.
 */
const PUBLIC_PLANS_CACHE_KEY = 'wp_public_plans_v1';

/** Planos públicos já carregados nesta sessão do navegador (render síncrono, sem esperar rede). */
export function getCachedPublicPlans(): CheckoutPlan[] | null {
  try {
    const raw = sessionStorage.getItem(PUBLIC_PLANS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? (parsed as CheckoutPlan[]) : null;
  } catch {
    return null;
  }
}

export async function fetchPublicPlans(): Promise<CheckoutPlan[]> {
  const res = await apiRequest<CheckoutPlan[] | { data?: CheckoutPlan[] }>('plans/public');
  const list = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : [];
  if (list.length) {
    try {
      sessionStorage.setItem(PUBLIC_PLANS_CACHE_KEY, JSON.stringify(list));
    } catch {
      // sem storage (aba privada etc.) — segue sem cache
    }
  }
  return list;
}

function buildAddressPayload(address: CheckoutAddressData) {
  return {
    street: address.street,
    number: address.number,
    complement: address.complement || undefined,
    neighborhood: address.neighborhood,
    city: address.city,
    state: address.state,
    zip_code: onlyDigits(address.zip_code),
    country: 'BR',
  };
}

export async function createSubscription(
  token: string,
  params: {
    planId: number;
    payment: CheckoutPaymentData;
    address: CheckoutAddressData;
    installments?: number;
  },
) {
  const [monthRaw, yearShort] = params.payment.expiration.split('/');
  const month = String(parseInt(monthRaw, 10)).padStart(2, '0');
  const nameParts = params.payment.cardName.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || firstName;
  const year = yearShort?.length === 2 ? `20${yearShort}` : yearShort;

  const body: Record<string, unknown> = {
    plan_id: params.planId,
    coupon_code: params.payment.couponCode?.trim() || undefined,
    card: {
      number: onlyDigits(params.payment.cardNumber),
      verification_value: params.payment.cvv,
      first_name: firstName,
      last_name: lastName,
      month,
      year,
    },
    address: buildAddressPayload(params.address),
  };

  if (params.installments && params.installments > 1) {
    body.installments = params.installments;
  }

  return apiRequest<Record<string, unknown>>('subscriptions/create', {
    method: 'POST',
    token,
    body: JSON.stringify(body),
  });
}

export interface CouponPreviewResponse {
  valid: boolean;
  coupon_code: string;
  discount_type: 'PERCENT' | 'FIXED';
  discount_value: number;
  original_price: number;
  discount_amount: number;
  final_price: number;
}

export async function previewCouponDiscount(
  token: string,
  code: string,
  planId: number,
) {
  return apiRequest<CouponPreviewResponse>(
    `coupons/preview/${encodeURIComponent(code)}?plan_id=${planId}`,
    { token },
  );
}

export async function fetchAddressByCep(cep: string) {
  const cleanCep = onlyDigits(cep);
  if (cleanCep.length !== 8) return null;

  const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
  const data = await response.json();
  if (data.erro) return null;

  return {
    street: data.logradouro || '',
    neighborhood: data.bairro || '',
    city: data.localidade || '',
    state: data.uf || '',
  };
}
